import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, X, ArrowUp, ArrowDown, Loader2, Save, Play, FileText, FileUp } from "lucide-react";
import { api, mediaUrl, thumbUrl, formatApiError } from "@/lib/api";

const EMPTY = {
  title: "", category_id: "", subcategory: "", description: "", year: "", tags: "",
  client: "", tools: "", notes: "", featured: false, status: "draft", display_order: 0, media: [],
};

export default function AdminItemForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (id) {
      api.get(`/admin/items/${id}`).then((r) => {
        const it = r.data;
        setForm({
          title: it.title, category_id: it.category_id, subcategory: it.subcategory || "",
          description: it.description || "", year: it.year || "", tags: (it.tags || []).join(", "),
          client: it.client || "", tools: (it.tools || []).join(", "), notes: it.notes || "",
          featured: !!it.featured, status: it.status, display_order: it.display_order || 0,
          media: it.media || [],
        });
      }).catch(() => toast.error("Could not load work item"));
    }
  }, [id]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setProgress(0);
    try {
      const r = await api.post("/admin/upload", fd, {
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      const uploaded = r.data.files.map((f, i) => ({
        storage_path: f.storage_path, media_type: f.media_type, content_type: f.content_type,
        original_filename: f.original_filename, thumb_path: f.thumb_path || "", alt_text: "", display_order: form.media.length + i,
      }));
      setForm((f) => ({ ...f, media: [...f.media, ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProgress(null);
    }
  };

  const moveMedia = (i, dir) => {
    const media = [...form.media];
    const j = i + dir;
    if (j < 0 || j >= media.length) return;
    [media[i], media[j]] = [media[j], media[i]];
    setForm({ ...form, media: media.map((m, k) => ({ ...m, display_order: k })) });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category_id) {
      toast.error("Choose a category first");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      year: form.year ? parseInt(form.year, 10) : null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      tools: form.tools.split(",").map((t) => t.trim()).filter(Boolean),
      display_order: parseInt(form.display_order, 10) || 0,
    };
    try {
      if (id) {
        await api.put(`/admin/items/${id}`, payload);
        toast.success("Work updated");
      } else {
        await api.post("/admin/items", payload);
        toast.success("Work created");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-surface-white border border-charcoal-900/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-copper-600";
  const labelCls = "block font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500 mb-1.5";

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">{id ? "Edit Work" : "Add Work"}</h1>
      <p className="text-sm text-charcoal-500 mb-8">Drafts stay invisible to visitors until published.</p>

      <form data-testid="item-form" onSubmit={save} className="flex flex-col gap-6 max-w-3xl">
        <div className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-6 flex flex-col gap-4">
          <div>
            <label className={labelCls} htmlFor="item-title">Title</label>
            <input id="item-title" data-testid="item-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Sri Sri Tattva Social Campaign" className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="item-category">Category</label>
              <select id="item-category" data-testid="item-category-select" required value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className={inputCls}>
                <option value="">Choose category...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="item-subcategory">Subcategory (optional)</label>
              <input id="item-subcategory" data-testid="item-subcategory-input" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Instagram" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="item-description">Description</label>
            <textarea id="item-description" data-testid="item-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="item-year">Year</label>
              <input id="item-year" data-testid="item-year-input" type="number" min="1990" max="2100" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2026" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="item-client">Client / Brand (optional)</label>
              <input id="item-client" data-testid="item-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="item-tags">Tags (comma separated)</label>
              <input id="item-tags" data-testid="item-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Social Media, Campaign, FMCG" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="item-tools">Tools used (comma separated)</label>
              <input id="item-tools" data-testid="item-tools-input" value={form.tools} onChange={(e) => setForm({ ...form, tools: e.target.value })} placeholder="Photoshop, Illustrator" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="item-notes">Notes (optional)</label>
            <input id="item-notes" data-testid="item-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
            <div>
              <label className={labelCls} htmlFor="item-status">Status</label>
              <select id="item-status" data-testid="item-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="item-order">Display order</label>
              <input id="item-order" data-testid="item-order-input" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} className={inputCls} />
            </div>
            <label className="flex items-center gap-2.5 col-span-2 cursor-pointer">
              <input
                data-testid="item-featured-checkbox"
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-4 h-4 accent-[#B85A2B]"
              />
              <span className="text-sm text-charcoal-800">Featured work</span>
            </label>
          </div>
        </div>

        <div className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-6">
          <p className={labelCls}>Media</p>
          <div
            data-testid="media-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
              dragging ? "border-copper-600 bg-copper-100/50" : "border-charcoal-900/20 hover:border-copper-600/50"
            }`}
          >
            <UploadCloud className="w-8 h-8 mx-auto text-copper-600/70" />
            <p className="mt-3 text-sm font-medium text-charcoal-800">Drag files here or browse</p>
            <p className="mt-1 text-xs text-charcoal-500">Images, videos (MP4/WebM), PDFs — multiple files supported</p>
            {progress !== null && (
              <div className="mt-4 max-w-xs mx-auto">
                <div className="h-1.5 rounded-full bg-charcoal-900/10 overflow-hidden">
                  <div className="h-full bg-copper-600 transition-[width]" style={{ width: `${progress}%` }} />
                </div>
                <p className="mt-1.5 font-mono text-[10px] text-charcoal-500">{progress}%</p>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            data-testid="media-file-input"
            type="file"
            multiple
            accept="image/*,video/mp4,video/webm,video/quicktime,application/pdf,.svg"
            className="hidden"
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          />

          {form.media.length > 0 && (
            <div className="mt-5 flex flex-col gap-2.5" data-testid="media-list">
              {form.media.map((m, i) => (
                <div key={m.storage_path} className="flex items-center gap-3 border border-charcoal-900/10 rounded-xl p-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface-muted shrink-0 flex items-center justify-center">
                    {m.media_type === "image" ? (
                      <img src={thumbUrl(m)} alt="" className="w-full h-full object-cover" />
                    ) : m.media_type === "video" ? (
                      <Play className="w-4 h-4 text-copper-600" />
                    ) : m.media_type === "pdf" ? (
                      <FileText className="w-4 h-4 text-copper-600" />
                    ) : (
                      <FileUp className="w-4 h-4 text-copper-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-charcoal-800 truncate">{m.original_filename}</p>
                    <input
                      data-testid={`media-alt-${i}`}
                      value={m.alt_text}
                      onChange={(e) => {
                        const media = [...form.media];
                        media[i] = { ...media[i], alt_text: e.target.value };
                        setForm({ ...form, media });
                      }}
                      placeholder="Alt text (optional)"
                      aria-label={`Alt text for ${m.original_filename}`}
                      className="mt-1 w-full bg-transparent border-b border-charcoal-900/10 text-xs text-charcoal-600 focus:outline-none focus:border-copper-600 py-0.5"
                    />
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button type="button" onClick={() => moveMedia(i, -1)} disabled={i === 0} aria-label="Move media up" className="p-1.5 text-charcoal-400 hover:text-copper-600 disabled:opacity-30">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveMedia(i, 1)} disabled={i === form.media.length - 1} aria-label="Move media down" className="p-1.5 text-charcoal-400 hover:text-copper-600 disabled:opacity-30">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`media-remove-${i}`}
                      onClick={() => setForm({ ...form, media: form.media.filter((_, k) => k !== i) })}
                      aria-label="Remove media"
                      className="p-1.5 text-charcoal-400 hover:text-red-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            data-testid="item-save-button"
            type="submit"
            disabled={saving || progress !== null}
            className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-7 py-3.5 hover:bg-copper-600 disabled:opacity-60 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : id ? "Save Changes" : "Save Work"}
          </button>
          <button
            type="button"
            data-testid="item-cancel-button"
            onClick={() => navigate("/admin")}
            className="rounded-full border border-charcoal-900/20 text-sm font-medium px-7 py-3.5 hover:border-copper-600 hover:text-copper-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
